import React from 'react';
import PropTypes from 'prop-types';
import UploadIcon from '../../../../../assets/icons/UploadIcon';
import Dropzone from 'react-dropzone';
/*
 * The part of a template that the operator has already typed, marked.
 *
 * A row is otherwise just a sentence, and with several of them on screen there
 * is nothing to say why any of them is being offered.
 */
const highlightMatch = (text, query) => {
    if (!query) return text;

    const at = text.toLowerCase().indexOf(query.toLowerCase());
    if (at === -1) return text;

    return (
        <React.Fragment>
            {text.slice(0, at)}
            <mark>{text.slice(at, at + query.length)}</mark>
            {text.slice(at + query.length)}
        </React.Fragment>
    );
};

import config from '../../../../../config';
import {editMessage} from '../../../../../services/chat/api';
import TranslateProperty from '../../../../../components/TranslateProperty';
import '../chat-css/MessageInput.css';
import uploadFile, {getFileType} from '../../../../../utils/FileService';
import TemplatesModal from "./TemplatesModal";
import {registerHotkeys} from "../../../../../utils/hotkeyListener";
import {hotkeyFor} from "../../../../../utils/hotkeys";
import Tooltip from "../../../../../components-new/general/Tooltip";
import Translate from "../../../../../components/Translate";
import {sendTypingMessage} from "../../../../../services/chat/api";
import colors from "../../../../../utils/Colors";
import {EmojiPicker} from "../../../../../components-new/general/EmojiPicker";
import XIcon from "../../../../../assets/icons/XIcon";
import EditSquareIcon from "../../../../../assets/icons/EditSquareIcon";
import SmileIcon from "../../../../../assets/icons/SmileIcon";
import ReplyIcon from "../../../../../assets/icons/ReplyIcon";
import SilverButton from "../../../../../components-new/buttons/SilverButton";
import LockKeyholeIcon from "../../../../../assets/icons/LockKeyholeIcon";
import LockKeyholeOpenIcon from "../../../../../assets/icons/LockKeyholeOpenIcon";
import PaperClipIcon from "../../../../../assets/icons/PaperClipIcon";
import FolderIcon from "../../../../../assets/icons/FolderIcon";
import BlueButton from "../../../../../components-new/buttons/BlueButton";
import SendHorizontalIcon from "../../../../../assets/icons/SendHorizontalIcon";
import KeyboardVoiceIcon from "../../../../../assets/icons/KeyboardVoiceIcon";
import ConfirmationModal from "../../../../../components-new/modals/ConfirmationModal";

const MAX_VOICE_RECORDING_MS = 30000;

/*
 * How long the recording bar stays on screen after the recording itself has
 * stopped, purely so it can be seen to leave.
 *
 * Must match voicePanelOut in MessageInput.css. Nothing audible waits on it -
 * the recorder is already stopped and the microphone already released by the
 * time this starts counting.
 */
const VOICE_PANEL_EXIT_MS = 180;

class MessageInput extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            dragIsActive: false,
            messageText: this.getStoredMessageText(),
            showEmojis: false,
            commentMessage: false,
            pendingFiles: [],
            isModalOpen: false,
            isRecordingVoice: false,
            /* the bar is still on screen, playing itself out - see
               VOICE_PANEL_EXIT_MS */
            voicePanelLeaving: false,
            voiceRecordingSeconds: 0,
            recordedVoiceFile: null,
            voiceConfirmOpen: false,
            sendingVoice: false,
        };
        this.textareaRef = React.createRef();
        this.emojiButtonRef = React.createRef();
        this.voiceCanvasRef = React.createRef();
        this.composerRef = React.createRef();
        this.typingTimeout = null;
        this.isTyping = false;
        this.mediaRecorder = null;
        this.microphoneStream = null;
        this.voiceChunks = [];
        this.voiceTimer = null;
        this.voiceTimeout = null;
        this.voiceLeaveTimeout = null;
        /* what the composer measured just before the field was taken away */
        this.voiceComposerHeight = null;
        this.voiceStoppedByLimit = false;
        this.voiceAudioContext = null;
        this.voiceAnalyser = null;
        this.voiceAnimationFrame = null;
        this.voiceAmplitudeHistory = [];
        this.voiceLastSampleAt = 0;
        this.voiceDiscardOnStop = false;
        this.unmounted = false;
    }

    /*
     * A textarea cannot size itself, so the height is set here: reset to auto so
     * scrollHeight reports what the content needs, then grown to that. Past the
     * max-height in the stylesheet the browser takes over and it scrolls - which
     * is why overflow is only turned on once that point is reached.
     */
    /*
     * Fits the field to what is written in it.
     *
     * The height has to be released before scrollHeight will say what the
     * content actually needs - and that is the whole difficulty, because reading
     * scrollHeight forces the browser to settle the style there and then. The
     * released height becomes a real, committed state, and a transition asked to
     * run from 'auto' to a pixel value does not run at all: height does not
     * interpolate against auto. So the field jumped however smooth the CSS said
     * it should be - most visibly when a long message was cleared in one go.
     *
     * The measuring is therefore done with the transition switched off, the old
     * height is put back and committed while it is still off, and only the final
     * value is set with the transition in hand. Nothing in between is painted.
     *
     * The same thing the widget does - see resizeTextAreaHeight there.
     */
    autoGrow = (immediate) => {
        const textarea = this.textareaRef.current;
        if (!textarea) return;

        const style = textarea.style;
        const previous = style.height;

        style.transition = 'none';
        style.height = 'auto';

        const max = parseInt(window.getComputedStyle(textarea).maxHeight, 10) || 140;
        const needed = textarea.scrollHeight - 10;

        style.height = previous;
        /* reading a layout property is what commits the line above; without it
           the browser coalesces the whole lot and the transition has nothing to
           start from */
        void textarea.offsetHeight;

        /*
         * immediate: the field is a new element and the height it is being
         * given is its first. There is nothing to grow from, so growing to it
         * is a flinch rather than a movement.
         */
        style.transition = immediate ? 'none' : '';
        style.height = Math.min(needed, max) + 'px';
        style.overflowY = needed > max ? 'auto' : 'hidden';

        if (immediate) {
            void textarea.offsetHeight;
            style.transition = '';
        }
    };

    componentDidMount() {
        this.focusTextareaToEnd();
        this.autoGrow();
        document.addEventListener('keydown', this.handleVoiceRecordingKeyDown);

        const {hotkeys, conversation, tariff} = this.props;
        const features = tariff && tariff.features ? tariff.features : [];

        /*
         * An internal chat has neither of these: a template is an answer to a
         * customer, and a private note is a note about a customer, hidden from
         * them. Neither button is drawn in an internal chat - the shortcuts were
         * the one way left to reach them, and they reached a state the screen has
         * no way to show.
         */
        const isInternalChat = conversation && conversation.platform === config.platformsMap.company;

        const actions = {OPEN_EMOJI_MODAL: () => this.toggleEmojis()};

        if (!isInternalChat) {
            /* the button is drawn on the same condition - see the toolbar below */
            if (features.includes(config.features.TEMPLATES)) {
                actions.OPEN_TEMPLATES_MODAL = () => this.handleTemplatesClick();
            }

            actions.SWITCH_PRIVATE_MESSAGE_MODE = () => this.handleCommentMessageChange();
        }

        this.cleanupHotkeys = registerHotkeys(hotkeys, actions);
    }

    componentWillUnmount() {
        this.unmounted = true;
        document.removeEventListener('keydown', this.handleVoiceRecordingKeyDown);
        this.clearVoiceTimers();
        this.stopVoiceVisualizer();
        if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
            this.mediaRecorder.onstop = null;
            this.mediaRecorder.stop();
        }
        this.stopMicrophoneTracks();

        if (this.cleanupHotkeys) {
            this.cleanupHotkeys();
        }
    }

    clearVoiceTimers = () => {
        clearInterval(this.voiceTimer);
        clearTimeout(this.voiceTimeout);
        clearTimeout(this.voiceLeaveTimeout);
        this.voiceTimer = null;
        this.voiceTimeout = null;
        this.voiceLeaveTimeout = null;
    };

    handleVoiceRecordingKeyDown = (event) => {
        if (!this.state.isRecordingVoice || event.repeat) return;

        if (event.key === 'Escape') {
            event.preventDefault();
            this.cancelVoiceRecording();
        } else if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.stopVoiceRecording(false);
        }
    };

    stopMicrophoneTracks = () => {
        if (this.microphoneStream) {
            this.microphoneStream.getTracks().forEach(track => track.stop());
            this.microphoneStream = null;
        }
    };

    startVoiceVisualizer = (stream) => {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;

        this.voiceAudioContext = new AudioContext();
        this.voiceAnalyser = this.voiceAudioContext.createAnalyser();
        this.voiceAnalyser.fftSize = 256;
        this.voiceAnalyser.smoothingTimeConstant = 0.72;
        this.voiceAudioContext.createMediaStreamSource(stream).connect(this.voiceAnalyser);

        const samples = new Uint8Array(this.voiceAnalyser.fftSize);
        this.voiceAmplitudeHistory = [];
        this.voiceLastSampleAt = 0;

        const draw = (timestamp) => {
            if (!this.voiceAnalyser) return;

            const canvas = this.voiceCanvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const ratio = window.devicePixelRatio || 1;
                const width = Math.max(1, Math.round(rect.width * ratio));
                const height = Math.max(1, Math.round(rect.height * ratio));
                if (canvas.width !== width || canvas.height !== height) {
                    canvas.width = width;
                    canvas.height = height;
                }

                const context = canvas.getContext('2d');
                this.voiceAnalyser.getByteTimeDomainData(samples);
                let energy = 0;
                for (let index = 0; index < samples.length; index += 1) {
                    const normalized = (samples[index] - 128) / 128;
                    energy += normalized * normalized;
                }
                const amplitude = Math.min(1, Math.sqrt(energy / samples.length) * 4.5);
                const maxBars = Math.ceil(rect.width / 5) + 2;

                if (!this.voiceLastSampleAt || timestamp - this.voiceLastSampleAt >= 55) {
                    this.voiceAmplitudeHistory.push(amplitude);
                    this.voiceLastSampleAt = timestamp;
                }

                if (this.voiceAmplitudeHistory.length > maxBars) {
                    this.voiceAmplitudeHistory = this.voiceAmplitudeHistory.slice(-maxBars);
                } else if (this.voiceAmplitudeHistory.length < maxBars) {
                    // Baseline bars represent the part of the recording timeline
                    // that has not received samples yet. Keeping them on the left
                    // lets the live waveform fill wide composers immediately.
                    this.voiceAmplitudeHistory = Array(maxBars - this.voiceAmplitudeHistory.length)
                        .fill(0)
                        .concat(this.voiceAmplitudeHistory);
                }

                context.clearRect(0, 0, width, height);
                context.fillStyle = '#d92d20';

                let x = width - 2 * ratio;
                for (let index = this.voiceAmplitudeHistory.length - 1; index >= 0 && x > 0; index -= 1) {
                    const level = this.voiceAmplitudeHistory[index];
                    const barWidth = (2 + level * 2.5) * ratio;
                    const barHeight = Math.max(3 * ratio, level * height * 0.9);
                    context.fillRect(x - barWidth, (height - barHeight) / 2, barWidth, barHeight);
                    x -= barWidth + 3 * ratio;
                }
            }

            this.voiceAnimationFrame = window.requestAnimationFrame(draw);
        };

        this.voiceAnimationFrame = window.requestAnimationFrame(draw);
    };

    stopVoiceVisualizer = () => {
        if (this.voiceAnimationFrame) window.cancelAnimationFrame(this.voiceAnimationFrame);
        this.voiceAnimationFrame = null;
        this.voiceAnalyser = null;
        this.voiceAmplitudeHistory = [];
        this.voiceLastSampleAt = 0;
        if (this.voiceAudioContext) {
            const closeResult = this.voiceAudioContext.close();
            if (closeResult && closeResult.catch) closeResult.catch(() => {
            });
        }
        this.voiceAudioContext = null;
    };

    getVoiceMimeType = () => {
        const candidates = [
            'audio/webm;codecs=opus',
            'audio/mp4',
            'audio/webm',
        ];

        return candidates.find(type => window.MediaRecorder.isTypeSupported(type)) || '';
    };

    startVoiceRecording = async () => {
        const {addNotification} = this.props;

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia || !window.MediaRecorder) {
            addNotification('voiceRecordingNotSupported', config.notificationLevels.error);
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({audio: true});
            if (this.unmounted) {
                stream.getTracks().forEach(track => track.stop());
                return;
            }

            const mimeType = this.getVoiceMimeType();
            const recorder = mimeType
                ? new window.MediaRecorder(stream, {mimeType})
                : new window.MediaRecorder(stream);

            this.microphoneStream = stream;
            this.mediaRecorder = recorder;
            this.voiceChunks = [];
            this.voiceStoppedByLimit = false;
            this.voiceDiscardOnStop = false;

            recorder.ondataavailable = event => {
                if (event.data && event.data.size > 0) this.voiceChunks.push(event.data);
            };

            recorder.onstop = this.handleVoiceRecordingStopped;
            recorder.start();
            this.startVoiceVisualizer(stream);

            /* a bar still leaving from the last recording has nothing left to
               say - the new one takes its place at once */
            clearTimeout(this.voiceLeaveTimeout);
            this.voiceLeaveTimeout = null;

            /* measured before the field goes, which is the only moment it still
               has the height the text gave it */
            this.voiceComposerHeight = this.composerRef.current
                ? this.composerRef.current.offsetHeight
                : null;

            this.setState({isRecordingVoice: true, voicePanelLeaving: false, voiceRecordingSeconds: 0});
            const startedAt = Date.now();
            this.voiceTimer = setInterval(() => {
                if (!this.unmounted) {
                    this.setState({
                        voiceRecordingSeconds: Math.min(30, Math.floor((Date.now() - startedAt) / 1000)),
                    });
                }
            }, 250);
            this.voiceTimeout = setTimeout(() => this.stopVoiceRecording(true), MAX_VOICE_RECORDING_MS);
        } catch (error) {
            this.stopMicrophoneTracks();
            addNotification('microphoneAccessDenied', config.notificationLevels.error);
        }
    };

    stopVoiceRecording = (stoppedByLimit = false) => {
        if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') return;

        this.voiceStoppedByLimit = stoppedByLimit;
        this.clearVoiceTimers();
        this.stopVoiceVisualizer();
        this.setState({
            isRecordingVoice: false,
            /*
             * Not an unmount yet. isRecordingVoice is what the recording is, and
             * it is false from this moment; this keeps the bar drawn for as long
             * as it takes to slide out, and nothing audible is waiting on it.
             */
            voicePanelLeaving: true,
            voiceRecordingSeconds: stoppedByLimit ? 30 : this.state.voiceRecordingSeconds
        });

        clearTimeout(this.voiceLeaveTimeout);
        this.voiceLeaveTimeout = setTimeout(() => {
            if (this.unmounted) return;

            this.voiceComposerHeight = null;
            this.setState({voicePanelLeaving: false}, () => {
                /*
                 * The field is a new element.
                 *
                 * It was unmounted for the length of the recording, so the
                 * height autoGrow had written on it went with it - it comes back
                 * at the 50px the stylesheet gives it, holding a message that
                 * needs three times that. Only the text survived, in state, so
                 * the height has to be worked out again from it - and it is put
                 * there at once, since a field that has only just appeared has
                 * nowhere to have grown from.
                 */
                this.autoGrow(true);
            });
        }, VOICE_PANEL_EXIT_MS);

        this.mediaRecorder.stop();
        this.stopMicrophoneTracks();
    };

    cancelVoiceRecording = () => {
        if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') return;
        this.voiceDiscardOnStop = true;
        this.stopVoiceRecording(false);
    };

    handleVoiceRecordingStopped = () => {
        if (this.unmounted) return;

        const recorder = this.mediaRecorder;
        const mimeType = (recorder && recorder.mimeType) || 'audio/webm';
        const extension = mimeType.indexOf('mp4') !== -1 ? 'm4a' : 'webm';
        const blob = new Blob(this.voiceChunks, {type: mimeType});

        this.mediaRecorder = null;
        this.voiceChunks = [];

        if (this.voiceDiscardOnStop) {
            this.voiceDiscardOnStop = false;
            this.setState({voiceRecordingSeconds: 0});
            return;
        }

        if (!blob.size) {
            this.props.addNotification('somethingWentWrong', config.notificationLevels.error);
            return;
        }

        const file = new File([blob], `voice-${Date.now()}.${extension}`, {type: mimeType});
        if (this.voiceStoppedByLimit) {
            this.setState({recordedVoiceFile: file, voiceConfirmOpen: true});
        } else {
            this.sendVoiceFile(file);
        }
    };

    sendVoiceFile = async (file) => {
        const {sendChatMessage, replyTo, addNotification} = this.props;
        const parentId = (replyTo && replyTo.id) || null;
        const {commentMessage} = this.state;
        const data = new FormData();
        data.append('file', file);

        this.setState({sendingVoice: true});
        try {
            const link = await uploadFile(data);
            sendChatMessage({url: link, type: getFileType(file.type), name: file.name, parentId}, commentMessage);
            if (this.props.setReplyTo) this.props.setReplyTo(null);
            return true;
        } catch (error) {
            if (error.response && error.response.status === 413) {
                addNotification('fileLarge', config.notificationLevels.error);
            } else {
                addNotification('somethingWentWrong', config.notificationLevels.error);
            }
            return false;
        } finally {
            if (!this.unmounted) this.setState({sendingVoice: false});
        }
    };

    confirmVoiceSend = async () => {
        const {recordedVoiceFile} = this.state;
        if (!recordedVoiceFile) return;

        const sent = await this.sendVoiceFile(recordedVoiceFile);
        if (sent && !this.unmounted) {
            this.setState({recordedVoiceFile: null, voiceConfirmOpen: false});
        }
    };

    discardRecordedVoice = () => {
        if (this.state.sendingVoice) return;
        this.setState({recordedVoiceFile: null, voiceConfirmOpen: false, voiceRecordingSeconds: 0});
    };

    notifyTypingStart = () => {
        const {conversation} = this.props;
        const {commentMessage} = this.state;

        if (conversation.platform === config.platformsMap.company) {
            return
        }

        if (!commentMessage) {
            if (!this.isTyping) {
                this.isTyping = true;
                sendTypingMessage(conversation.id, "start")
            }
            clearTimeout(this.typingTimeout);
            this.typingTimeout = setTimeout(() => {
                this.notifyTypingStop();
            }, 3000);
        }

    };

    notifyTypingStop = () => {
        const {conversation} = this.props;

        if (conversation.platform === config.platformsMap.company) {
            return
        }

        if (this.isTyping) {
            this.isTyping = false;
            sendTypingMessage(conversation.id, "stop")
        }
        clearTimeout(this.typingTimeout);
    };

    handleChange = (event) => {
        const messageText = event.target.value;
        const trimmedText = messageText.trim().toLowerCase();

        this.setState({messageText}, () => {
            this.storeMessageText(messageText);
            this.autoGrow();
        });

        this.notifyTypingStart();

        if (trimmedText.length > 1) {
            const {templates} = this.props;

            const filteredTemplates = templates.filter(t => {
                const text = t.text.toLowerCase();
                const folder = (t.folderName || '').toLowerCase();
                return text.includes(trimmedText) || folder.includes(trimmedText);
            });

            this.props.setSugTemplates(filteredTemplates, trimmedText);
        } else {
            this.props.setSugTemplates([]);
        }
    };

    getStoredMessageText = () => {
        const {conversation} = this.props;
        const storedMessageText = sessionStorage.getItem(conversation.id);
        return storedMessageText ? storedMessageText : '';
    };

    storeMessageText = (text) => {
        const {conversation} = this.props;
        sessionStorage.setItem(conversation.id, text);
    };

    addEmojiToTextarea = (emoji) => {
        this.setState((prevState) => ({
            messageText: prevState.messageText + emoji
        }), () => {
            this.storeMessageText(this.state.messageText);
            this.autoGrow();
        });
    };

    toggleEmojis = () => {
        this.setState((prevState) => ({
            showEmojis: !prevState.showEmojis
        }));
    };

    /*
     * Called from the conversation when a message is chosen for editing. The
     * field takes the message's text and says what it is doing; sending then
     * edits that message rather than writing a new one.
     */
    beginEdit = (message) => {
        this.setState({messageText: message.text || ''}, this.focusTextareaToEnd);
    };

    /* empties the field without touching what it was for */
    clearDraft = () => {
        this.setState({messageText: ''}, () => {
            this.storeMessageText('');
            this.autoGrow();
            this.focusTextareaToEnd();
        });
    };

    cancelEdit = () => {
        this.props.setEditingMessage(null);
        this.clearDraft();
    };

    submitEdit = () => {
        const {editingMessage, setEditingMessage, addNotification} = this.props;
        const {messageText} = this.state;

        if (!messageText.trim()) return;

        editMessage(editingMessage.id, messageText)
            .then(() => {
                // the message object is what the list renders from
                editingMessage.text = messageText;
                editingMessage.edited = true;
                setEditingMessage(null);
                // back to one line: it was as tall as the message just saved
                this.setState({messageText: ''}, this.autoGrow);
                addNotification('messageEdited', config.notificationLevels.info);
            })
            .catch((error) => {
                const status = error.response && error.response.status;
                if (status === 423) {
                    addNotification('actionBlockedByAdmin', config.notificationLevels.warning);
                } else if (status === 409) {
                    addNotification('notYourMessage', config.notificationLevels.warning);
                } else {
                    addNotification('somethingWentWrong', config.notificationLevels.error);
                }
            });
    };

    selectTemplate = (templateText) => {
        this.setState((prevState) => {
            const newText = templateText;
            this.storeMessageText(newText);
            this.props.setSugTemplates([]);
            return {messageText: newText};
        }, this.focusTextareaToEnd);
    };

    changeMessageTextToTemplate = (templateText) => {
        this.setState((prevState) => {
            const newText = prevState.messageText + templateText;
            return {messageText: newText};
        }, this.focusTextareaToEnd);
    };

    onDragEnter = () => {
        this.setState({
            dragIsActive: true,
        });
    };

    onDragLeave = () => {
        this.setState({
            dragIsActive: false,
        });
    };

    handleKeyPress = (event) => {
        if (event.charCode === 13 && !event.shiftKey) {
            event.preventDefault();
            // the same key, doing whatever the field is currently for
            if (this.props.editingMessage) {
                this.submitEdit();
            } else {
                this.sendTextMessage();
            }
        }
    };

    handleSendClick = () => {
        if (this.props.editingMessage) {
            this.submitEdit();
        } else {
            this.sendTextMessage();
        }
    };

    sendTextMessage = async () => {
        const {sendChatMessage, replyTo, setReplyTo, addNotification} = this.props;
        const {messageText, commentMessage, pendingFiles} = this.state;
        const parentId = (replyTo && replyTo.id) || null;
        const parentWebChatUUID = (replyTo && replyTo.webChatUUID) || null;

        // skip if nothing to send
        if (!messageText.trim() && pendingFiles.length === 0) return;

        // send text message first (if any)
        if (messageText.trim()) {
            const message = {
                type: config.messageTypes.text,
                text: messageText,
                parentId,
                parentWebChatUUID
            };
            sendChatMessage(message, commentMessage);
        }

        for (const pending of pendingFiles) {
            const data = new FormData();
            data.append('file', pending.file);
            try {
                const link = await uploadFile(data);
                const attachmentMessage = {
                    url: link,
                    type: pending.type,
                    name: pending.name,
                    parentId
                };
                sendChatMessage(attachmentMessage, commentMessage);
            } catch (error) {
                if (error.response && error.response.status === 413) {
                    addNotification('fileLarge', config.notificationLevels.error);
                } else {
                    addNotification('somethingWentWrong', config.notificationLevels.error);
                }
            }
        }

        // reset state after sending
        this.setState({
            messageText: '',
            pendingFiles: [],
        }, this.autoGrow);

        this.storeMessageText('');
        this.props.setSugTemplates([]);
        setReplyTo(null);

        if (commentMessage) {
            this.notifyTypingStop();
        } else {
            this.isTyping = false;
        }
    };


    uploadAttachment = (files) => {
        this.setState(prevState => ({
            dragIsActive: false,
            pendingFiles: [...prevState.pendingFiles, ...files.map(file => ({
                file,
                name: file.name,
                type: getFileType(file.type),
                uploaded: false, // not uploaded yet
                url: null
            }))]
        }));
        this.focusTextareaToEnd();
    };

    handleTemplatesClick = () => {
        this.setState({
            isModalOpen: true
        })
    };

    handleCommentMessageChange = () => {
        this.setState((prevState) => ({
            commentMessage: !prevState.commentMessage
        }));
    };

    /*
     * The caret to the end, and the box to the size of what is in it.
     *
     * autoGrow runs on input, which is every path where the operator types - and
     * none of the paths where the text arrives ready-made: a template picked from
     * the list, a message loaded for editing, a draft restored. Those left the
     * field at its old height with overflow hidden, so a long template simply had
     * no scrollbar and its tail could not be reached.
     */
    focusTextareaToEnd = () => {
        this.autoGrow();

        const textarea = this.textareaRef.current;
        if (textarea) {
            textarea.focus();
            textarea.selectionStart = textarea.selectionEnd = textarea.value.length;
        }
    };

    removePendingFile = (indexToRemove) => {
        this.setState((prevState) => ({
            pendingFiles: prevState.pendingFiles.filter((_, index) => index !== indexToRemove)
        }));
    };

    render() {
        const {
            dragIsActive, commentMessage, messageText, pendingFiles, isModalOpen,
            isRecordingVoice, voicePanelLeaving, voiceRecordingSeconds, voiceConfirmOpen, sendingVoice
        } = this.state;

        /*
         * The bar is on screen - recording, or still sliding out of the way.
         *
         * Everything the bar replaces has to wait for this, not for
         * isRecordingVoice: the composer is a row, and a field coming back while
         * the bar is still leaving lands beside it.
         */
        const voicePanelVisible = isRecordingVoice || voicePanelLeaving;
        const {
            tariff, user, replyTo, setReplyTo, templates, conversation, editingMessage,
            sugTemplates = [], sugQuery, sugActiveIndex, onPickTemplate,
            onCloseTemplates, hotkeys = []
        } = this.props;
        const {features} = tariff;
        /* the icons say little on their own - the tooltip names them, and carries
           the shortcut for the ones that have one */
        const keyFor = (action) => hotkeyFor(hotkeys, action);
        const canSend = Boolean(messageText.trim() || pendingFiles.length > 0);

        return (
            <React.Fragment>

                <Dropzone
                    disableClick
                    className="ignore"
                    multiple={false}
                    onDragEnter={this.onDragEnter}
                    onDragLeave={this.onDragLeave}
                    onDrop={this.uploadAttachment}
                    /*
                     * min-height, not height. A fixed 150px was fine while the
                     * box inside never changed size; now that it grows with the
                     * text, anything past 150px - which the row of controls is
                     * part of - fell outside the drop zone and disappeared.
                     * The minimum is what keeps the drag target usable when
                     * there is nothing typed.
                     */
                    style={{
                        position: 'relative',
                        minHeight: '72px',
                    }}
                >
                    {dragIsActive ? (
                        <div className="centered-flex-container full-height"
                             style={{backgroundColor: 'var(--ds-bg-primary)'}}>
                            <UploadIcon width={36} height={36} style={{color: colors.silverDark}}
                                        stroke="currentColor"/>
                        </div>
                    ) : (
                        <div className="message-input">

                            {this.state.showEmojis && (
                                <EmojiPicker
                                    anchorRef={this.emojiButtonRef}
                                    onSelect={this.addEmojiToTextarea}
                                    onClose={() => this.setState({showEmojis: false})}
                                />
                            )}

                            <div className="message-input__content">
                                {/* Editing, said where the editing happens. The
                                    field is the one part of the conversation that
                                    does not scroll away, which is why the editor
                                    moved here out of the bubble. */}
                                {editingMessage && (
                                    <div className="input-edit-banner">
                                        <EditSquareIcon
                                            className="input-edit-banner__icon"
                                            strokeWidth={1.5}
                                            width={15}
                                            height={15}
                                            stroke="currentColor"
                                        />

                                        <div className="input-edit-banner__body">
                                            <span className="input-edit-banner__title">
                                                <Translate text="editMessage"/>
                                            </span>
                                            <span className="input-edit-banner__text">
                                                {editingMessage.text || ''}
                                            </span>
                                        </div>

                                        <button type="button" className="input-edit-banner__remove"
                                                onClick={this.cancelEdit}>
                                            <XIcon stroke="currentColor" width={12} height={12}/>
                                        </button>
                                    </div>
                                )}

                                {replyTo && (
                                    <div className="input-reply-preview">
                                        <ReplyIcon
                                            className="input-reply-preview__icon"
                                            strokeWidth={1.5}
                                            width={15}
                                            height={15}
                                            stroke="currentColor"
                                        />

                                        <div className="input-reply-preview__text">
                                            {['FILE', 'VIDEO', 'PHOTO', 'AUDIO'].includes(replyTo.type)
                                                ? <Translate text="attachment"/>
                                                : replyTo.text || ''}
                                        </div>

                                        <button
                                            type="button"
                                            className="input-reply-preview__remove"
                                            onClick={() => {
                                                setReplyTo(null);
                                                this.focusTextareaToEnd();
                                            }}
                                        >
                                            <XIcon stroke="currentColor" width={12} height={12}/>
                                        </button>
                                    </div>
                                )}
                                {this.state.pendingFiles.length > 0 && (
                                    <div className="pending-attachments">
                                        {this.state.pendingFiles.map((file, index) => (
                                            <span className="pending-file" key={index}>
                                                <PaperClipIcon
                                                    className="pending-file__icon"
                                                    stroke="currentColor"
                                                    width={14}
                                                    height={14}
                                                />
                                                <Tooltip text={file.name} needTranslate={false}
                                                         style={{minWidth: 0, flex: 1}}>
                                                    <span className="pending-file__name">{file.name}</span>
                                                </Tooltip>
                                                <button
                                                    type="button"
                                                    className="pending-file__remove"
                                                    onClick={() => {
                                                        this.removePendingFile(index);
                                                        this.focusTextareaToEnd();
                                                    }}
                                                >
                                                    <XIcon stroke="currentColor" width={12} height={12}/>
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                <div
                                    ref={this.composerRef}
                                    /*
                                     * Held at the height it had when the field
                                     * was taken away.
                                     *
                                     * The composer grows with the text in it, so
                                     * starting a recording over a long message
                                     * collapsed it back to its resting 72px and
                                     * the whole conversation jumped up. The
                                     * widget keeps the size; so does this.
                                     */
                                    style={voicePanelVisible && this.voiceComposerHeight
                                        ? {minHeight: this.voiceComposerHeight + 'px'}
                                        : undefined}
                                    className={`message-input__composer ${commentMessage ? 'message-input__composer--comment' : ''} ${isRecordingVoice ? 'message-input__composer--recording' : ''}`}>
                                    {/* Anchored to the field, not to the box around
                                        it: a reply, an edit or a row of attachments
                                        opens above the field and used to carry this
                                        list up with it. */}
                                    {!voicePanelVisible && sugTemplates.length > 0 && (
                                        <div className="sug-templates">
                                            <div className="sug-templates__head">
                                                <span className="sug-templates__title">
                                                    <Translate text="templates"/>
                                                    <span className="sug-templates__count">{sugTemplates.length}</span>
                                                </span>
                                                <span className="sug-templates__hint">
                                                    <kbd>↑</kbd><kbd>↓</kbd>
                                                    <Translate text="sugTemplatesPick"/>
                                                </span>

                                                <button type="button" className="sug-templates__close"
                                                        onClick={onCloseTemplates}>
                                                    <XIcon stroke="currentColor" width={12} height={12}/>
                                                </button>
                                            </div>

                                            <div
                                                key={sugTemplates.map(t => t.id).join('-')}
                                                className="sug-templates__list"
                                            >
                                                {sugTemplates.map((item, index) => (
                                                    <div
                                                        key={`${item.id}-${index}`}
                                                        id={`sug-template-${index}`}
                                                        onClick={() => onPickTemplate(item.text)}
                                                        className={`sug-templates__item ${sugActiveIndex === index ? 'is-active' : ''}`}
                                                    >
                                                        {item.folderName && (
                                                            <span
                                                                className="sug-templates__folder">{item.folderName}</span>
                                                        )}
                                                        <span className="sug-templates__text">
                                                            {highlightMatch(item.text, sugQuery)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {voicePanelVisible && (
                                        <div className={'message-input__recording-panel' + (voicePanelLeaving ? ' is-leaving' : '')}>
                                            <span className="message-input__recording-dot"/>
                                            <canvas
                                                ref={this.voiceCanvasRef}
                                                className="message-input__voice-waveform"
                                                aria-hidden="true"
                                            />
                                            <span className="message-input__voice-time">
                                                0:{String(voiceRecordingSeconds).padStart(2, '0')}
                                            </span>
                                            <div className="message-input__recording-actions">
                                                <SilverButton
                                                    className="message-input__recording-cancel"
                                                    variant="ghost"
                                                    iconOnly
                                                    expandedHover
                                                    width="28px"
                                                    height="28px"
                                                    onClick={this.cancelVoiceRecording}
                                                >
                                                    <XIcon width={18} height={18} stroke="currentColor"/>
                                                </SilverButton>

                                                <BlueButton
                                                    className="message-input__send"
                                                    type="button"
                                                    iconOnly
                                                    width="40px"
                                                    height="40px"
                                                    styles={{borderRadius: '50%'}}
                                                    onClick={() => this.stopVoiceRecording(false)}
                                                >
                                                    <SendHorizontalIcon strokeWidth={1.5} width={18} height={18}
                                                                        stroke="currentColor"/>
                                                </BlueButton>

                                            </div>
                                        </div>
                                    )}

                                    {!voicePanelVisible && <div className="message-input__actions">
                                        {conversation.platform !== config.platformsMap.company &&
                                            <Tooltip
                                                text="commentMessage"
                                                shortcut={keyFor('SWITCH_PRIVATE_MESSAGE_MODE')}
                                                style={{display: 'flex'}}
                                            >
                                                <SilverButton
                                                    className={`message-input__action message-input__comment-toggle ${commentMessage ? 'is-active' : ''}`}
                                                    variant="ghost" iconOnly expandedHover
                                                    width="20px" height="20px"
                                                    onClick={this.handleCommentMessageChange}>
                                                    {commentMessage
                                                        ? <LockKeyholeIcon width={18} height={18} stroke="currentColor"/>
                                                        : <LockKeyholeOpenIcon width={18} height={18}
                                                                               stroke="currentColor"/>}
                                                </SilverButton>
                                            </Tooltip>
                                        }

                                        <Tooltip
                                            text="emoji"
                                            shortcut={keyFor('OPEN_EMOJI_MODAL')}
                                            style={{display: 'flex'}}
                                        >
                                            <SilverButton className="message-input__action" variant="ghost" iconOnly
                                                          expandedHover
                                                          width="20px" height="20px" ref={this.emojiButtonRef}
                                                          onClick={this.toggleEmojis}>
                                                <SmileIcon strokeWidth={1.5} width={18} height={18}
                                                           stroke="currentColor"/>
                                            </SilverButton>
                                        </Tooltip>

                                        {features.includes(config.features.FILE_SHARING) && (
                                            <Tooltip text="attachFile" style={{display: 'flex'}}>
                                                <Dropzone className="ignore message-input__dropzone" multiple={false}
                                                          onDrop={this.uploadAttachment}>
                                                    <SilverButton className="message-input__action" variant="ghost"
                                                                  iconOnly expandedHover
                                                                  width="20px" height="20px">
                                                        <PaperClipIcon strokeWidth={1.5} width={18} height={18}
                                                                       stroke="currentColor"/>
                                                    </SilverButton>
                                                </Dropzone>
                                            </Tooltip>
                                        )}

                                        {features.includes(config.features.TEMPLATES) && conversation.platform !== config.platformsMap.company && (
                                            <Tooltip
                                                text="templates"
                                                shortcut={keyFor('OPEN_TEMPLATES_MODAL')}
                                                style={{display: 'flex'}}
                                            >
                                                <SilverButton className="message-input__action" variant="ghost" iconOnly
                                                              expandedHover
                                                              width="20px" height="20px"
                                                              onClick={this.handleTemplatesClick}>
                                                    <FolderIcon strokeWidth={1.5} width={18} height={18}
                                                                stroke="currentColor"/>
                                                </SilverButton>
                                            </Tooltip>
                                        )}
                                    </div>}

                                    {!voicePanelVisible && (
                                        <TranslateProperty properties={[{
                                            key: 'placeholder',
                                            value: 'pleaseFillMessage2'
                                        }]}>
                            <textarea
                                id="chat-message-input"
                                ref={this.textareaRef}
                                className="message-input-text"
                                value={messageText}
                                onKeyPress={this.handleKeyPress}
                                /*
                                 * No onInput beside this.
                                 *
                                 * Both map to the same native input event, so
                                 * the field was resized twice per keystroke -
                                 * once here and once from handleChange when the
                                 * state had settled. The second call opens by
                                 * switching the transition off to take its
                                 * measurement, which killed the animation the
                                 * first one had just started. One call, after
                                 * the state is in, is all it needs.
                                 */
                                onChange={this.handleChange}
                            />
                                        </TranslateProperty>
                                    )}

                                    {/*
                                      * One slot, two buttons stacked in it.
                                      *
                                      * The first attempt grew the send button from
                                      * nothing so that the microphone beside it was
                                      * pushed along rather than jumping - and that
                                      * worked, but width is a layout property: every
                                      * frame of it re-wrapped the text in the field
                                      * next door, and on a long message the whole
                                      * thing crawled.
                                      *
                                      * Nothing in the layout moves now. The slot is
                                      * always 40px, both buttons fill it, and they
                                      * cross over in place - which is only opacity
                                      * and transform, neither of which costs a
                                      * reflow. It is also the more literal reading
                                      * of where the microphone belongs: not beside
                                      * the send button but in the same place, with
                                      * whichever one applies on top.
                                      */}
                                    {!voicePanelVisible && (
                                        <div className="message-input__send-slot">
                                            {features.includes(config.features.FILE_SHARING) &&
                                            (conversation.platform === config.platformsMap.company ||
                                                this.props.messageConfig.operatorCanSendVoiceMessage) && !editingMessage && (
                                                <SilverButton
                                                    className={'message-input__voice' + (canSend ? ' is-covered' : '')}
                                                    variant="ghost"
                                                    iconOnly
                                                    expandedHover
                                                    width="30px"
                                                    height="30px"
                                                    disabled={sendingVoice || canSend}
                                                    onClick={this.startVoiceRecording}
                                                >
                                                    <KeyboardVoiceIcon width={20} height={20}/>
                                                </SilverButton>
                                            )}

                                            <BlueButton
                                                className={'message-input__send' + (canSend ? ' is-visible' : '')}
                                                type="button"
                                                iconOnly
                                                width="40px"
                                                height="40px"
                                                disabled={!canSend}
                                                styles={{borderRadius: '50%'}}
                                                onClick={this.handleSendClick}
                                            >
                                                <SendHorizontalIcon strokeWidth={1.5} width={18} height={18}
                                                                    stroke="currentColor"/>
                                            </BlueButton>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </Dropzone>

                {isModalOpen &&
                    <TemplatesModal
                        isOpen={isModalOpen}
                        cancelAction={() => this.setState({isModalOpen: false})}
                        templates={templates}
                        user={user}
                        changeMessageTextToTemplate={this.changeMessageTextToTemplate}
                    />
                }

                {voiceConfirmOpen && (
                    <ConfirmationModal
                        isOpen={voiceConfirmOpen}
                        loading={sendingVoice}
                        onClose={this.discardRecordedVoice}
                        onConfirm={this.confirmVoiceSend}
                        icon={<KeyboardVoiceIcon width={20} height={20} fill="var(--ds-fg-secondary)"/>}
                        message={<Translate text="voiceRecordingLimitReached"/>}
                        secondaryText={<Translate text="voiceRecordingConfirmSend"/>}
                    />
                )}

            </React.Fragment>

        );
    }
}

MessageInput.propTypes = {
    sendChatMessage: PropTypes.func.isRequired,
    user: PropTypes.object.isRequired,
    conversation: PropTypes.object.isRequired,
    messageConfig: PropTypes.object.isRequired,
};

export default MessageInput;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/chat/chat-components/MessageInput.jsx